import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface MobileShellContextValue {
  mobileMenuOpen: boolean;
  filterSheetOpen: boolean;
  openMobileMenu: () => void;
  closeMobileMenu: () => void;
  toggleMobileMenu: () => void;
  openFilterSheet: () => void;
  closeFilterSheet: () => void;
  toggleFilterSheet: () => void;
}

const MobileShellContext = createContext<MobileShellContextValue | null>(null);

export const MobileShellProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);
  const closeFilterSheet = useCallback(() => setFilterSheetOpen(false), []);

  const openMobileMenu = useCallback(() => {
    setFilterSheetOpen(false);
    setMobileMenuOpen(true);
  }, []);

  const openFilterSheet = useCallback(() => {
    setMobileMenuOpen(false);
    setFilterSheetOpen(true);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setFilterSheetOpen(false);
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const toggleFilterSheet = useCallback(() => {
    setMobileMenuOpen(false);
    setFilterSheetOpen((prev) => !prev);
  }, []);

  const value = useMemo(
    () => ({
      mobileMenuOpen,
      filterSheetOpen,
      openMobileMenu,
      closeMobileMenu,
      toggleMobileMenu,
      openFilterSheet,
      closeFilterSheet,
      toggleFilterSheet,
    }),
    [
      mobileMenuOpen,
      filterSheetOpen,
      openMobileMenu,
      closeMobileMenu,
      toggleMobileMenu,
      openFilterSheet,
      closeFilterSheet,
      toggleFilterSheet,
    ]
  );

  return <MobileShellContext.Provider value={value}>{children}</MobileShellContext.Provider>;
};

export function useMobileShellContext(): MobileShellContextValue {
  const ctx = useContext(MobileShellContext);
  if (!ctx) {
    throw new Error('useMobileShellContext must be used within MobileShellProvider');
  }
  return ctx;
}

export function useOptionalMobileShellContext(): MobileShellContextValue | null {
  return useContext(MobileShellContext);
}
